import { useEffect, useState } from "react";
import { gql, useMutation, useLazyQuery, useQuery } from "@apollo/client";
import { notify, notifyError } from "../../../utils/toast";
import clsx from "clsx";
import { GET_STORE_LIST } from "../store-signup-request/page";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import {
  AdminStoreTableWithMobileCards,
  type AdminStoreListItem,
} from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";
import type {
  GetStoreListQuery,
  OrdersByStoreIdQuery,
  ConfirmOrderAsAutoConfirmMutation,
  ConfirmOrderAsAutoConfirmMutationVariables,
} from "../../../__generated__/graphql";

const ORDERS_BY_STORE_ID = gql(`
  query ordersByStoreId($storeId: Int!) {
    ordersByStoreId(storeId: $storeId) {
      id
      user {
        id
        name
        phone
      }
      isCanceled
      isPackConfirmed
      totalQuantity
      amount
      createdAt
    }
  }
`);

const CONFIRM_ORDER_AS_AUTO_CONFIRM_MUTATION = gql(`
  mutation ConfirmOrderAsAutoConfirm($orderId: Int!) {
    confirmOrderAsAutoConfirm(orderId: $orderId) {
      ok
      error
    }
  }
`);

type StoreRow = NonNullable<
  NonNullable<GetStoreListQuery["getStoreList"]>["result"]
>[number];

type OrderRow = NonNullable<
  OrdersByStoreIdQuery["ordersByStoreId"]
>[number];

const ConfirmPreOrderAsAutoConfirmDetail = () => {
  const [searchKeyword, setSearchKeyword] = useState("");
  const { data: storeData } = useQuery<GetStoreListQuery>(GET_STORE_LIST);
  const [selectedStore, setSelectedStore] = useState<StoreRow | null>(null);
  const [
    getOrdersByStoreId,
    { data: ordersData, loading: ordersLoading, refetch: refetchOrders },
  ] = useLazyQuery<OrdersByStoreIdQuery>(ORDERS_BY_STORE_ID, {
    variables: { storeId: selectedStore?.id ?? 0 },
  });
  const [confirmOrderAsAutoConfirm] = useMutation<
    ConfirmOrderAsAutoConfirmMutation,
    ConfirmOrderAsAutoConfirmMutationVariables
  >(CONFIRM_ORDER_AS_AUTO_CONFIRM_MUTATION);
  const [selectedOrder, setSelectedOrder] = useState<number | null>(null);

  useScrollToDetailPanelOnSelect(selectedStore);

  useEffect(() => {
    if (selectedStore?.id) {
      getOrdersByStoreId({ variables: { storeId: selectedStore.id } });
    }
  }, [selectedStore?.id, getOrdersByStoreId]);

  const fullList =
    storeData?.getStoreList?.ok ? storeData.getStoreList.result ?? [] : [];
  const keyword = searchKeyword.trim().toLowerCase();
  const storeList = keyword
    ? fullList.filter(
        (store) =>
          store.title.toLowerCase().includes(keyword) ||
          (store.address?.toLowerCase().includes(keyword) ?? false),
      )
    : fullList;

  const handleSelectStore = (store: AdminStoreListItem) => {
    const full = storeList.find((s) => s.id === store.id) ?? null;
    setSelectedStore(full);
  };

  const handleSelectOrder = (orderId: number) => {
    setSelectedOrder(orderId);
  };

  const handleConfirmOrderAsAutoConfirm = async (orderId: number) => {
    if (!window.confirm("해당 고객이 맞습니까?")) {
      return;
    }
    await confirmOrderAsAutoConfirm({
      variables: { orderId },
      onCompleted: (data) => {
        if (data.confirmOrderAsAutoConfirm.ok) {
          notify("주문이 확정(자동확정)되었습니다.");
          setSelectedOrder(null);
          refetchOrders();
        } else {
          notifyError(data.confirmOrderAsAutoConfirm.error ?? "확정에 실패했습니다.");
          refetchOrders();
        }
      },
    });
  };

  const rightPanelContent =
    selectedStore && ordersData ? (
      <div className="space-y-4">
        <p className="text-gray-700 text-sm">
          선택 매장: <strong>{selectedStore.title}</strong>
        </p>
        <div className="block text-sm font-medium text-gray-700 mt-2">
          3. 주문 선택 후 확정
        </div>
        {ordersLoading && <p className="text-gray-500 text-sm">로딩 중...</p>}
        {ordersData.ordersByStoreId.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full whitespace-nowrap divide-y divide-gray-200 text-sm">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">
                      주문자
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">
                      주문일시
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">
                      주문금액
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">
                      취소/확정
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">
                      선택
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ordersData.ordersByStoreId.map((order: OrderRow) => (
                    <tr key={order.id}>
                      <td className="px-3 py-2">
                        {order.user?.name} ({order.user?.phone})
                      </td>
                      <td className="px-3 py-2">
                        {order.createdAt
                          ? new Date(order.createdAt).toLocaleString()
                          : "—"}
                      </td>
                      <td className="px-3 py-2">{order.amount}</td>
                      <td className="px-3 py-2">
                        <span
                          className={clsx(
                            "px-2 inline-flex leading-5 font-semibold rounded-full text-xs",
                            {
                              "bg-green-100 text-green-800": order.isCanceled,
                              "bg-gray-100 text-gray-800": !order.isCanceled,
                            },
                          )}
                        >
                          {order.isCanceled ? "취소" : "결제완료"}
                        </span>
                        <span
                          className={clsx(
                            "ml-1 px-2 inline-flex leading-5 font-semibold rounded-full text-xs",
                            {
                              "bg-green-100 text-green-800":
                                order.isPackConfirmed,
                              "bg-gray-100 text-gray-800": !order.isPackConfirmed,
                            },
                          )}
                        >
                          {order.isPackConfirmed ? "확정" : "미확정"}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => handleSelectOrder(order.id)}
                          className={clsx(
                            "p-2 rounded text-sm font-medium",
                            selectedOrder === order.id
                              ? "bg-gray-500 text-white"
                              : "bg-blue-500 text-white hover:bg-blue-600",
                          )}
                        >
                          선택
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {selectedOrder != null && (
              <button
                type="button"
                onClick={() => handleConfirmOrderAsAutoConfirm(selectedOrder)}
                className="w-full mt-4 p-4 bg-amber-400 text-gray-900 rounded-lg text-center font-bold hover:bg-amber-500"
              >
                확정시키기
              </button>
            )}
          </>
        ) : (
          <p className="text-gray-500 text-sm">해당 매장의 주문이 없습니다.</p>
        )}
      </div>
    ) : selectedStore && ordersLoading ? (
      <p className="text-gray-500 text-sm">주문 목록 로딩 중...</p>
    ) : null;

  return (
    <AdminListDetailLayout
      title="주문 확정 시키기 (자동확정)"
      description="매장 선택 후 주문을 확정합니다. (DB 수정, 친구추천 쿠폰 발급, 리뷰 스케줄러 등록 포함)"
      leftPanel={{
        title: "1. 매장 검색 및 선택",
        searchPlaceholder: "매장 검색 (이름, 주소, 지역)",
        searchValue: searchKeyword,
        onSearchChange: setSearchKeyword,
        children: (
          <AdminStoreTableWithMobileCards
            stores={storeList}
            selectionMode="single"
            selectedIds={selectedStore ? [selectedStore.id] : []}
            onSelect={handleSelectStore}
            emptyText="등록된 매장이 없습니다."
          />
        ),
      }}
      rightPanel={{
        title: "2. 주문 선택 및 확정",
        emptyText: "왼쪽 목록에서 매장을 선택하세요.",
        children: rightPanelContent,
      }}
    />
  );
};

export default ConfirmPreOrderAsAutoConfirmDetail;
